/**
 * useDomainMutation — wrapper sobre `useMutation` que injeta:
 *
 *  1. onError default: Sentry.captureException + console.error estruturado +
 *     toast de erro (via MutationsProvider).
 *  2. onSuccess default: invalidate de queryKeys declarados + toast de sucesso.
 *  3. Tags Sentry (`feature`, `action`) opcionais por mutation.
 *
 * Resolve CF-8 do audit-architecture-20260430-1558 ("178 mutations sem
 * onError"). Cumpre IR-OBS-003 (todo erro -> Sentry com tags).
 *
 * Padrao alvo:
 *
 *   const create = useDomainMutation({
 *     mutationFn: (input: LeadInsert) => createLead(input),
 *     invalidates: qk.sales.leads.all,
 *     toast: { success: "Lead criado", error: "Erro ao criar lead" },
 *     tags: { feature: "sales", action: "create_lead" },
 *     onSuccess: (data) => notifyStaff(data.id),  // hooks adicionais opcionais
 *   });
 *
 * Caller pode sobrescrever onError/onSuccess passando explicitamente — nesse
 * caso wrapper ainda faz invalidate e toast, mas chama callback do caller
 * APOS o handler default. Para opt-out total use `useMutation` direto + ESLint
 * disable comentado com motivo.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type QueryKey,
} from "@tanstack/react-query";
import { Sentry, toSentryError } from "@pps/observability";
import { useMutationsContext } from "./MutationsProvider";
import type {
  InvalidateInput,
  MutationTags,
  MutationToastConfig,
} from "./types";
import { recusaDePermissao } from "./rls-message";

export interface UseDomainMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
> extends Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "onError" | "onSuccess"
  > {
  /** QueryKey ou array de QueryKeys a invalidar pos-success. */
  invalidates?: InvalidateInput;
  /** Configuracao de toast. Passe `false` para suprimir ambos. */
  toast?: MutationToastConfig | false;
  /** Tags injetadas no Sentry.captureException. */
  tags?: MutationTags;
  /** Hook adicional pos-success — chamado APOS invalidate + toast default. */
  onSuccess?: (
    data: TData,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<unknown>;
  /** Hook adicional pos-error — chamado APOS Sentry + toast default. */
  onError?: (
    error: TError,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<unknown>;
}

const DEFAULT_ERROR_TITLE = "Erro ao processar operacao";

/**
 * `@supabase/postgrest-js` (>=2.x) so envolve o erro em `PostgrestError`
 * (subclasse de `Error`) no caminho `.throwOnError()` — no padrao usado em
 * toda a base (`const { error } = await supabase.rpc(...); if (error) throw
 * error;`), `error` eh o objeto plano `JSON.parse(body)` da resposta HTTP
 * (`PostgrestBuilder.processResponse`), NUNCA uma instancia de `Error`.
 * `error instanceof Error` falha silenciosamente pra esse caso e o fallback
 * `String(error)` produz o literal "[object Object]" no toast — daí a
 * checagem por `.message` além de `instanceof Error`.
 */
function extractErrorMessage(error: unknown): string {
  // RECUSA DE PERMISSAO VEM PRIMEIRO. O texto do Postgres
  // (`new row violates row-level security policy for table "club_lessons"`)
  // chegava inteiro ao toast — em ingles e com nome de tabela. Ver
  // rls-message.ts para por que a traducao mora AQUI e nao na tela.
  const recusa = recusaDePermissao(error);
  if (recusa) return recusa.mensagem;

  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return String(error);
}

/**
 * Normaliza `InvalidateInput` para `QueryKey[]` de forma DETERMINISTICA.
 *
 * Discriminador: `input` eh `QueryKey[]` se e somente se for nao-vazio E todos
 * os elementos forem arrays. Caso contrario eh um unico `QueryKey` (que tambem
 * eh um array de segmentos `unknown`, mas seus elementos NAO sao todos arrays
 * — sao strings/numbers/objects/etc por convencao do `@pps/query-keys`).
 *
 * Eliminada a heuristica antiga (`Array.isArray(first) || typeof first === "object"`)
 * que dava falso-positivo quando o primeiro segmento de uma `QueryKey` era um
 * objeto de filtros (ex.: `qk.sales.leads.list(orgId, filters)` ->
 * `["sales","leads","list", orgId, { status, search }]` — primeiro segmento
 * `"sales"` eh string, mas se algum dia o factory mudasse, a heuristica
 * silenciosamente trataria a key inteira como lista de keys e invalidaria
 * cada segmento como uma key independente).
 */
function asArray(input: InvalidateInput | undefined): QueryKey[] {
  if (!input) return [];
  if (!Array.isArray(input)) {
    // Defensivo: TS impede chegar aqui (QueryKey eh ReadonlyArray<unknown>),
    // mas em runtime caller pode passar `null` cast como any — nao quebrar.
    return [];
  }
  if (input.length === 0) return [];
  // QueryKey[] sse TODOS os elementos sao arrays. Caso contrario QueryKey unica.
  const isArrayOfKeys = input.every((el) => Array.isArray(el));
  return isArrayOfKeys ? (input as QueryKey[]) : [input as QueryKey];
}

export function useDomainMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseDomainMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();
  const { toast } = useMutationsContext();

  const {
    invalidates,
    toast: toastConfig,
    tags,
    onSuccess: callerOnSuccess,
    onError: callerOnError,
    ...rest
  } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    onSuccess: async (data, variables, context) => {
      // 1. Invalidate
      const keys = asArray(invalidates);
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key });
      }

      // 2. Toast de sucesso (so se config explicito)
      if (toastConfig !== false && toastConfig?.success && toast) {
        toast({
          title: toastConfig.success,
          description: toastConfig.successDescription,
          variant: "success",
        });
      }

      // 3. Hook caller
      if (callerOnSuccess) {
        await callerOnSuccess(data, variables, context);
      }
    },
    onError: async (error, variables, context) => {
      // 1. Sentry — IR-OBS-003: todo erro relevante com tags.
      //
      // `toSentryError` porque o erro que chega aqui quase nunca eh um `Error`:
      // eh o objeto plano do postgrest-js (mesma razao documentada em
      // `extractErrorMessage` abaixo). Sem promover, o Sentry titula a issue
      // com o nome MINIFICADO da funcao que chamou o capture e a mensagem
      // "Object captured as exception with keys: code, details, hint, message"
      // — agrupando por call site em vez de por erro (PLAYPROSPORT-16).
      //
      // Promove SO o que vai pro Sentry: o erro propagado pro caller
      // (`onError`, `mutateAsync`) continua sendo o original.
      try {
        Sentry.captureException(toSentryError(error), tags ? { tags } : undefined);
      } catch {
        // Sentry pode nao estar inicializado em testes — falhar silencioso
      }

      // 2. Console estruturado (IR-OBS-006 compativel)
      // eslint-disable-next-line no-console
      console.error("[useDomainMutation]", {
        feature: tags?.feature,
        action: tags?.action,
        error,
      });

      // 3. Toast de erro (se nao suprimido + provider disponivel)
      if (toastConfig !== false && toast) {
        const message =
          (typeof toastConfig === "object" && toastConfig?.error) ||
          DEFAULT_ERROR_TITLE;
        const description = extractErrorMessage(error);
        toast({
          title: message,
          description,
          variant: "error",
        });
      }

      // 4. Hook caller
      if (callerOnError) {
        await callerOnError(error, variables, context);
      }
    },
  });
}
